import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { GasSizeText } from '../../core/sizes/gas-size.enum';

import { GasProgressColor } from './gas-progress.interface';

/** @see https://www.figma.com/file/YDxRp3ZZaGFI4UoB6mCcBr/GAS20_Administration_Vote?node-id=246%3A18411 */

const SUCCESS_PROGRESS = 100;
const MIN_PROGRESS = 0;
const DEFAULT_OFFSET = 4;
const PROGRESS_PRESET = {
  200: {
    name: 'xxl',
    width: 7,
    radius: 195,
  },
  80: {
    name: GasSizeText.ExtraLarge,
    width: 6,
    radius: 37,
  },
  48: {
    name: GasSizeText.Large,
    width: 4,
    radius: 22,
  },
  32: {
    name: GasSizeText.Small,
    width: 4,
    radius: 14,
  },
};

@Component({
  selector: 'gas-progress',
  templateUrl: './gas-progress.component.html',
  styleUrls: ['./gas-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasProgressComponent implements OnInit, OnChanges {

  @Input()
  public color: GasProgressColor = 'brand';

  @Input()
  public progress = 0;

  @Input()
  public size: 200 | 80 | 48 | 32 = 80;

  public progressSetting = {
    fullLength: 0,
    offset: '',
    progress: '',
    radius: 0,
    width: 0,
  };

  public name: 'xxl' | 'xlg' | 'lg' | 'sm' = 'xlg';

  public ngOnInit(): void {
    this.setNameAndWidth(this.size);
    this.calculatedProgressSetting();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes?.progress ||
      changes?.color ||
      changes?.size
    ) {
      this.setNameAndWidth(this.size);
      this.calculatedProgressSetting();
      this.calculatedProgress();
    }
  }

  /**
   * calculatedProgress - метод для расчета процента выполнения прогресса
   * progress - промежуточная переменная в которую записывается текущий прогресс
   * progressDash - промежуточная переменная в которую записывается значение длина круга за минусом прогресса
   * this.progressSetting.progress - хранит в себе актуальные данные по прогрессу
   * this.progressSetting.offset - смещение для начала закрашивания прогресса.
   * Всегда равно 70, кроме когда прогресс = 100. Тогда смещение делаем равное 0
   */
  public calculatedProgress(): void {
    if (this.progress < MIN_PROGRESS || this.progress > SUCCESS_PROGRESS) {
      throw new Error('Параметр progress должен находиться в диапазоне от 0 до 100');
    }

    const emptyProgress = this.progress === MIN_PROGRESS;
    const offset = this.progress === SUCCESS_PROGRESS ? MIN_PROGRESS : DEFAULT_OFFSET;

    const progress = Math.round((this.progressSetting.fullLength * this.progress) / SUCCESS_PROGRESS + offset);
    const progressDash = this.progressSetting.fullLength - progress;

    this.progressSetting.progress = emptyProgress ? `0 ${this.progressSetting.fullLength}` : `${progress} ${progressDash}`;
    this.progressSetting.offset = progress === this.progressSetting.fullLength ? '0' : this.progressSetting.offset;
  }

  /**
   * calculatedProgressSetting - метод по расчету начальных параметров прогресса
   * в зависимости от размера круга (200px, 80px, 48px, 32px)
   */
  public calculatedProgressSetting(): void {
    this.progressSetting.fullLength = Math.round(2 * Math.PI * this.progressSetting.radius);
    if (this.name === 'xxl') {
      this.progressSetting.offset = Math.round(this.size * 1.5).toString();
    } else {
      this.progressSetting.offset = Math.round(this.size * 0.75).toString();
    }
    this.calculatedProgress();
  }

  public setNameAndWidth(size: number): void {
    this.name = PROGRESS_PRESET[size].name;
    this.progressSetting.width = PROGRESS_PRESET[size].width;
    this.progressSetting.radius = PROGRESS_PRESET[size].radius;
  }

}
